/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Ring } from "./Ring";
import { useTranslation } from "react-i18next";

import backButton from "./left_arrow.png";
import closeButton from "./close_button.png";

import "./NetPromoterScoreSurvey.scss";
import loading from "./loading-inverse.gif";
import { NpsScore } from "./NpsScore";
import { NpsReviewTopics } from "./NpsReviewTopics";

export function NetPromoterScoreSurvey(props) {
  const { t, i18n } = useTranslation();
  const { customer, requestId } = useParams();

  const { modal, surveyData, customerData, show, initialRating } = props;
  const onHideProps = props.onHide;

  const starsRef = useRef();
  const timerDebounce = useRef();
  const autosaveInFlight = useRef(false);
  const autosavePending = useRef(false);
  const autosaveScheduled = useRef(null);

  async function waitForAutosave(timeoutMs = 3000) {
    const start = Date.now();
    while (autosaveInFlight.current) {
      if (Date.now() - start > timeoutMs) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [sent, setSent] = useState(false);
  const [notificationInfo, setNotificationInfo] = useState(null);
  const [rating, setRating] = useState(-1);
  const [comment, setComment] = useState("");
  const [topics, setTopic] = useState([]);

  function debounce(method, delay) {
    if (timerDebounce.current) clearTimeout(timerDebounce.current);
    timerDebounce.current = setTimeout(function () {
      method();
    }, delay);
  }

  const getData = async () => {
    setIsLoading(true);

    var customerData = await axios.get(
      `${process.env.REACT_APP_API_URL}/${customer}/info`
    );

    setCustomerInfo(customerData.data);

    try {
      var notification = await axios.get(
        `${process.env.REACT_APP_API_URL}/${customer}/nps/${requestId}`
      );

      setNotificationInfo(notification.data);

      setIsLoading(false);
    } catch (err) {
      if (err.response.status == 404) {
        setNotFound(true);
        setIsLoading(false);
      }
    }
  };

  const sendResponse = async (partial = false) => {
    if (
      rating === -1 &&
      !topics?.filter((x) => typeof x.score !== "undefined").length
    )
      return;
    if (partial) {
      if (autosaveInFlight.current) {
        // Mark that another autosave is needed and drop intermediates
        autosavePending.current = true;
        return;
      }
      autosaveInFlight.current = true;
      setIsAutosaving(true);
    }
    if (!partial) {
      autosavePending.current = false;
      await waitForAutosave();
      setIsSubmitting(true);
    }
    await axios.post(
      `${process.env.REACT_APP_API_URL}/${customer}/nps/${notificationInfo._id}`,
      {
        rating,
        comment,
        partial,
        topics,
      }
    );
    if (!partial) setIsSubmitting(false);

    if (!partial) setSent(true);

    if (partial) {
      autosaveInFlight.current = false;
      setIsAutosaving(false);
      if (autosavePending.current) {
        // Only schedule one more autosave with the latest state
        autosavePending.current = false;
        if (autosaveScheduled.current) clearTimeout(autosaveScheduled.current);
        autosaveScheduled.current = setTimeout(() => {
          autosaveScheduled.current = null;
          sendResponse(true);
        }, 800);
      }
    }
  };

  useEffect(() => {
    if (!modal) getData();
    else {
      setNotificationInfo(surveyData);
      setCustomerInfo(customerData);

      if (initialRating && show) {
        starsRef &&
          starsRef.current &&
          starsRef.current.setRating(initialRating);
      } else {
        starsRef?.current?.setRating(-1);
      }
    }
  }, [customer, requestId, surveyData, customerData, show]);

  useEffect(() => {
    if (notificationInfo) setTopic(notificationInfo.survey.topics);
  }, [notificationInfo]);

  useEffect(() => {
    if (customerInfo?.settings?.locale) {
      i18n.changeLanguage(customerInfo.settings.locale);
    }
  }, [customerInfo]);

  useEffect(() => {
    if (!modal) document.body.classList.toggle("nps", true);
  }, []);

  useEffect(() => {
    const hasScoredTopic = topics?.some((x) => typeof x?.score !== "undefined");
    if (rating >= 0 || hasScoredTopic) {
      debounce(() => {
        sendResponse(true);
      }, 800);
    }
  }, [rating, comment, topics]);

  const onHide = () => {
    if (sent || window.confirm(t("confirm_cancel"))) onHideProps();
  };

  function onScoreClick(topic) {
    const exists = topics.find(
      (item) => item.description === topic.description
    );
    if (!exists) {
      return setTopic((prev) => [...prev, topic]);
    }
    setTopic((prev) =>
      prev.map((item) =>
        item.description === topic.description ? topic : item
      )
    );
  }

  return (
    <>
      {modal && (
        <div
          className={`nps-overlay ${show ? "show" : ""}`}
          onClick={() => {
            onHide();
          }}
        ></div>
      )}

      <div className={`nps-page ${modal ? "modal" : ""} ${show ? "show" : ""}`}>
        {!modal && (
          <div className="logo">
            <img
              src={customerInfo.logo}
              alt={t("alt.customer_logo", { name: customerInfo.name })}
            />
          </div>
        )}

        {modal && (
          <>
            {!sent && (
              <button
                className="back-button"
                onClick={() => {
                  onHide();
                }}
              >
                <img alt={t("alt.back")} src={backButton} />
              </button>
            )}
            {sent && (
              <button
                className="close-button"
                onClick={() => {
                  onHide();
                }}
              >
                <img alt={t("alt.close")} src={closeButton} />
              </button>
            )}
          </>
        )}

        {isLoading && (
          <div className="loading-container">
            <Ring />
          </div>
        )}

        {notificationInfo && (
          <>
            {!sent ? (
              <div className="nps-form">
                <p>{notificationInfo.survey.npsQuestion}</p>

                <NpsScore
                  ref={starsRef}
                  enabled={true}
                  onChange={(rating) => {
                    setRating(rating);
                  }}
                />

                {rating >= 0 && topics?.length > 0 && (
                  <NpsReviewTopics
                    survey={notificationInfo.survey}
                    onScoreClick={onScoreClick}
                    reviewedTopics={topics}
                  />
                )}

                {rating >= 0 && (
                  <>
                    <p>{notificationInfo.survey.commentsQuestion}</p>

                    <textarea
                      name="comment"
                      value={comment}
                      onChange={(e) => {
                        setComment(e.target.value);
                      }}
                    ></textarea>
                  </>
                )}

                <button
                  className="submit-button"
                  disabled={rating === -1 || rating < 0 || rating > 10 || isSubmitting || isAutosaving}
                  onClick={() => {
                    setIsSubmitting(true);
                    debounce(() => {
                      sendResponse(false);
                    }, 500);
                  }}
                >
                  {t("submit_response")}{" "}
                  {isSubmitting && <img alt={t("alt.loading")} src={loading} />}
                </button>
              </div>
            ) : (
              <div className="success-message">
                <h2>{t("success_title")}</h2>
                <p>{t("thanks_for_participation")}</p>
              </div>
            )}
          </>
        )}

        {notFound && !isLoading && (
          <div className="error-message">
            <h2>{t("survey_closed_title")}</h2>
            <p>{t("survey_closed_message")}</p>
          </div>
        )}
      </div>
    </>
  );
}
